import React, { Fragment, useState, useEffect } from "react";
import { styled } from "@mui/material/styles";
import { useDispatch, useSelector } from "react-redux";
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";

import {
  Box,
  FormControl,
  FormControlLabel,
  MenuItem,
  Select,
  Typography,
  Radio,
  RadioGroup
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";

import DatePickerCustom from "../../../shared/general/DatePickerCustom";
import TextFieldTheme from "../../../shared/general/TextFieldTheme";
import ButtonBlue from "../../../shared/general/ButtonBlue";
import DrawerCustom from "../../../shared/general/Drawer";

import {
  postHoliday,
  putHoliday,
  getAllHolidayAndAllHolidayPattern
} from "../../../../../actions/holiday";

const StyledRoot = styled("div")({
  width: 450,
  padding: 24,
  "& .GridTopicInput": {
    display: "flex",
    alignItems: "center",
  },
});

const StyledFooter = styled("div")({
  padding: 16,
  display: "flex",
  justifyContent: "flex-end",
  "& .cancel": {
    marginRight: 8,
  },
});
const StyledWrapInput = styled("div")({
  marginBottom: 8,
});

const StyledContentLabel = styled(Typography)({
  fontWeight: 600,
  fontSize: 16,
});

const FormControlStyled = styled(FormControl)((disabled) => ({
  "& .MuiFilledInput-root": {
    backgroundColor: "#919eab14",
    height: 56,
    padding: "0px 12px",
    borderRadius: 8,
    "&.Mui-focused": {
      backgroundColor: "#919eab14",
    },
    "& .MuiSelect-select": {
      padding: "4px 0 5px 8px",
    },
    "&:hover": {
      ...(disabled ? {} : { backgroundColor: "#919eab29" }),
      "&:before": {
        border: "none !important",
      },
    },
    "&::after": {
      border: "none",
    },
    "&::before": {
      border: "none",
      borderBottomStyle: "none !important",
    },
  },
}));

const MenuItemAddHolidayPattern = styled(MenuItem)({
  padding: "0",
  margin: "16px 16px 6px",
  borderRadius: "8px",
  backgroundColor: "#007afe",
  color: "#ffffff",
  "&:hover":{
    backgroundColor: '#0046b7'
  },
  "&.Mui-selected":{
    backgroundColor: '#007afe',
    "&:hover":{
      backgroundColor: '#0046b7'
    },
  }
});

const EditHoliday = (props) => {
  const dispatch = useDispatch();
  const { addModal, handleClose, handleOpenAlert : handleOpenAlertAPI } = props;
  const { result: holidayStore } = useSelector((state) => state.holiday);
  const { t, i18n } = useTranslation();
  const [listHolidayPattern, setListHolidayPattern] = useState([]);

  const { control, handleSubmit, formState: { errors, dirtyFields }, reset, watch, clearErrors, setError, setValue } = useForm({
    defaultValues : {
      name: "",
      name_EN: "",
      dateHoliday: null,
      compensateName: "",
      compensateName_EN: "",
      compensateDate: null,
      idHoliday: null,
      idHolidayPattern: "",
      holidayPatternName: "",
      year: dayjs().format("YYYY"),
      isActive: "1",
      isHavingCompensateHoliday: "0"
    },
  });

  useEffect(() => {
    if (addModal.isOpen) {
      let filterValue = {};
      
      if (addModal.type === "Edit" && addModal.data) {
        filterValue = {
          name: addModal.data.name ? addModal.data.name : "",
          name_EN: addModal.data.name_EN ? addModal.data.name_EN : "",
          dateHoliday: addModal.data.dateHoliday,
          compensateName: addModal.data.compensateName
            ? addModal.data.compensateName
            : "",
          compensateName_EN: addModal.data.compensateName_EN
            ? addModal.data.compensateName_EN
            : "",
          compensateDate: addModal.data.compensateDate
            ? addModal.data.compensateDate
            : null,
          idHoliday: addModal.data.idHoliday,
          idHolidayPattern: addModal.data.idHolidayPattern,
          holidayPatternName: addModal.data.holidayPatternName,
          year: dayjs(addModal.data.dateHoliday).format("YYYY"),
          isActive: addModal.data.isActive ? "1" : "0",
          isHavingCompensateHoliday: addModal.data.compensateDate ? "1" : "0"
        };
      } else {
        filterValue = {
          name: "",
          name_EN: "",
          dateHoliday: null,
          compensateName: "",
          compensateName_EN: "",
          compensateDate: null,
          idHoliday: null,
          idHolidayPattern: "",
          holidayPatternName: "",
          year: dayjs().format("YYYY"),
          isActive: "1",
          isHavingCompensateHoliday: "0"
        };
      }

      filterHolidayPattern();

      reset(filterValue);
    }
  }, [addModal.isOpen]);

  useEffect(() => {
    if(addModal && addModal.isOpen){
      filterHolidayPattern();
    }
  },[watch("year")]);

  const filterHolidayPattern = () => {
    let year = watch("year");

    if(year){
      if(
        holidayStore && 
        Object.keys(holidayStore).length > 0 && 
        holidayStore[year]
      ){
        setListHolidayPattern(holidayStore[year]);
      }else{
        setListHolidayPattern([]);
      }
    }
  };

  const onSubmit = async (formdata) => {
    let isError = false;

    if(formdata.idHolidayPattern === "new" && (!formdata.holidayPatternName || formdata.holidayPatternName.length <= 0) ){
      setError("holidayPatternName", { type: "custom", message: t("PleaseFillInRequiredInformations") });
      isError = true;
    }

    if(!formdata.name || typeof(formdata.name) !== "string" || formdata.name.length <= 0){
      setError("name", { type: "custom", message: t("PleaseFillInRequiredInformations") });
      isError = true;
    }

    if(formdata.name_EN === ""){
      formdata.name_EN = null;
    }

    if(!formdata.dateHoliday || !dayjs(formdata.dateHoliday).isValid()){
      setError("dateHoliday", { type: "custom", message: t("PleaseFillInRequiredInformations") });
      isError = true;
    }

    if(formdata.dateHoliday && formdata.year && dayjs(formdata.dateHoliday).format("YYYY") !== formdata.year){
      setError("dateHoliday", { type: "custom", message: t("PleaseEnterADateThatMatchesTheSelectedYear") });
      isError = true;
    }

    if(formdata.isHavingCompensateHoliday === "1"){
      if(!formdata.compensateName || formdata.compensateName.length <= 0){
        setError("compensateName", { type: "custom", message: t("PleaseFillInRequiredInformations") });
        isError = true;
      }

      if(!formdata.compensateDate || !dayjs(formdata.compensateDate).isValid){
        setError("compensateDate", { type: "custom", message: t("PleaseFillInRequiredInformations") });
        isError = true;
      }

      if(formdata.compensateDate && formdata.year && dayjs(formdata.compensateDate).format("YYYY") !== formdata.year){
        setError("compensateDate", { type: "custom", message: t("PleaseEnterADateThatMatchesTheSelectedYear") });
        isError = true;
      }

      if(formdata.compensateName_EN === ""){
        formdata.compensateName_EN = null;
      }
    }else{
      formdata.compensateName = null;
      formdata.compensateName_EN = null;
      formdata.compensateDate = null;
    }
    
    if(!isError){
      formdata.dateHoliday = dayjs(formdata.dateHoliday).format("YYYY-MM-DD");
      if(formdata.compensateDate && dayjs(formdata.compensateDate).isValid()){
        formdata.compensateDate = dayjs(formdata.compensateDate).format("YYYY-MM-DD");
      }

      delete formdata.isHavingCompensateHoliday;
      
      if(addModal.type === "Edit"){
        let idHoliday = formdata.idHoliday;
        delete formdata.idHoliday;
        delete formdata.holidayPatternName;

        const result = await dispatch(putHoliday(idHoliday, formdata));
        if (result && result.status == 200) {
          handleOpenAlertAPI({ alertType : "success" });
          handleClose();
          dispatch(
            getAllHolidayAndAllHolidayPattern(dayjs(new Date()).format("YYYY"))
          );
        } else {
          handleOpenAlertAPI({ alertType : "error" });
        }
      }else if(addModal.type === "Add"){
        const result = await dispatch(postHoliday(formdata));
        if (result && result.status == 200) {
          handleOpenAlertAPI({ alertType : "success" });
          handleClose();
          dispatch(
            getAllHolidayAndAllHolidayPattern(dayjs(new Date()).format("YYYY"))
          );
        } else {
          handleOpenAlertAPI({ alertType : "error" });
        }
      }
    }
  };

  const checkIsChosePattern = () => {
    if(watch("idHolidayPattern") === ""){
      return false;
    }else{
      return true;
    }
  };

  return (
    <DrawerCustom
      title={`${t("HolidayDetails")}`}
      anchor="right"
      open={addModal.isOpen}
      onClose={handleClose}
    >
      <StyledRoot>
        <form onSubmit={handleSubmit(onSubmit)}>
          <StyledWrapInput>
            <StyledContentLabel color="text.third" gutterBottom>
              {t("SelectYear")}
            </StyledContentLabel>
            <FormControlStyled variant="filled" fullWidth>
              <Controller 
                control={control}
                name="year"
                render={({ field }) => (
                  <Select
                    {...field}
                  >
                    <MenuItem value={dayjs().format("YYYY")}>{dayjs().format(i18n.language === "th" ? "BBBB" : "YYYY")}</MenuItem>
                    <MenuItem value={dayjs().add(1,'year').format("YYYY")}>{dayjs().add(1,'year').format(i18n.language === "th" ? "BBBB" : "YYYY")}</MenuItem>
                  </Select>
                )}
              />
            </FormControlStyled>
          </StyledWrapInput>
          <StyledWrapInput>
            <StyledContentLabel color="text.third" gutterBottom>
              {t("HolidayPattern")}
            </StyledContentLabel>
            {watch("year") ? 
              <FormControlStyled variant="filled" fullWidth>
                <Controller 
                  control={control}
                  name="idHolidayPattern"
                  render={({ field }) => (
                    <Select
                      {...field}
                      displayEmpty
                      disabled={listHolidayPattern.length <= 0 ? true : false}
                      readOnly={addModal.type === "Edit" ? true : false}
                      onChange={(e) => {
                        field.onChange(e);
                        if(e.target.value !== "new" && watch("holidayPatternName")){
                          setValue("holidayPatternName", "");
                        }
                      }}
                    >
                      {listHolidayPattern.length > 0 ? (
                        listHolidayPattern.map((hp) => (
                          <MenuItem key={hp.idHolidayPattern} value={hp.idHolidayPattern}>
                            {hp.holidayPatternName}
                          </MenuItem>
                        ))
                      ) : (
                        <Fragment></Fragment>
                      )}
                      <MenuItemAddHolidayPattern 
                        value="new"
                      >
                        <Box 
                          width="100%"
                          display="flex" 
                          alignItems="center" 
                          justifyContent="center"
                          padding="8px 0px"
                        >
                          <AddIcon style={{ marginRight: "8px" }} />{t("AddHolidayPattern")}
                        </Box>
                      </MenuItemAddHolidayPattern>
                    </Select>
                  )}
                />
              </FormControlStyled>
              : <></>
            }
          </StyledWrapInput>
          {watch("idHolidayPattern") === "new" ? (
              <StyledWrapInput>
                <StyledContentLabel color="text.third" gutterBottom>
                  {t("NameOfHolidayPattern")}
                </StyledContentLabel>
                <Controller 
                  control={control}
                  name="holidayPatternName"
                  render={({ field }) => (
                    <TextFieldTheme
                      {...field}
                      variant="outlined"
                      multiline
                      error={(errors && errors.holidayPatternName) ? true : false}
                      helperText={errors && errors.holidayPatternName && errors.holidayPatternName.message || ""}
                    />
                  )}
                />
              </StyledWrapInput>
            )
            : (<Fragment></Fragment>)
          }
          <StyledWrapInput>
            <StyledContentLabel color={checkIsChosePattern() ? "text.third" : "#AFB1B1"} gutterBottom>
              {t("HolidayName")}
            </StyledContentLabel>
            <Controller 
              control={control}
              name="name"
              render={({ field }) => (
                <TextFieldTheme
                  {...field}
                  variant="outlined"
                  multiline
                  error={(errors && errors.name) ? true : false}
                  helperText={errors && errors.name && errors.name.message || ""}
                  disabled={!checkIsChosePattern()}
                />
              )}
            />
          </StyledWrapInput>
          <StyledWrapInput>
            <StyledContentLabel color={checkIsChosePattern() ? "text.third" : "#AFB1B1"} gutterBottom>
              {t("HolidayNameEnglish")}
            </StyledContentLabel>
            <Controller 
              control={control}
              name="name_EN"
              render={({ field }) => (
                <TextFieldTheme
                  {...field}
                  variant="outlined"
                  multiline
                  disabled={!checkIsChosePattern()}
                />
              )}
            />
          </StyledWrapInput>
          <StyledWrapInput>
            <StyledContentLabel color={checkIsChosePattern() ? "text.third" : "#AFB1B1"} gutterBottom>
              {t("DateHoliday")}
            </StyledContentLabel>
            <Controller 
              control={control}
              name="dateHoliday"
              render={({ field }) => (
                <DatePickerCustom
                  {...field}
                  openTo="day"
                  inputFormat={`DD MMM ${i18n.language === "th" ? "BBBB" : "YYYY"}`}
                  inputProps={{
                    placeholder: i18n.language === "th" ? "วว ดดด ปปปป" : "DD MMM YYYY",
                    readOnly: true
                  }}
                  disableMaskedInput
                  error={(errors && errors.dateHoliday) ? true : false} 
                  helperText={errors && errors.dateHoliday && errors.dateHoliday.message || ""}
                  disabled={!checkIsChosePattern()}
                />
              )}
            />
          </StyledWrapInput>
          <StyledWrapInput>
            <StyledContentLabel color={checkIsChosePattern() ? "text.third" : "#AFB1B1"} gutterBottom>
              {t("Use")}
            </StyledContentLabel>
            <Controller 
              control={control}
              name="isActive"
              render={({ field }) => (
                <FormControl
                  disabled={!checkIsChosePattern()}
                >
                  <RadioGroup
                    {...field}
                    row
                  >
                    <FormControlLabel
                      value={"1"}
                      control={<Radio />}
                      label={`${t("Use")}`}
                    />
                    <FormControlLabel
                      value={"0"}
                      control={<Radio />}
                      label={`${t("NotInUse")}`}
                    />
                  </RadioGroup>
                </FormControl>
              )}
            />
          </StyledWrapInput>
          <StyledWrapInput>
            <StyledContentLabel color={checkIsChosePattern() ? "text.third" : "#AFB1B1"} gutterBottom>
              {t("HavingCompensateHoliday")}
            </StyledContentLabel>
            <Controller 
              control={control}
              name="isHavingCompensateHoliday"
              render={({ field }) => (
                <FormControl
                  disabled={!checkIsChosePattern()}
                >
                  <RadioGroup
                    {...field}
                    onChange={(event) => {
                      field.onChange(event);

                      if(errors){
                        if(errors.compensateName){
                          clearErrors("compensateName");
                        }
                        if(errors.compensateDate){
                          clearErrors("compensateDate");
                        }
                      }
                    }}
                    row
                  >
                    <FormControlLabel
                      value={"1"}
                      control={<Radio />}
                      label={`${t("Have")}`}
                    />
                    <FormControlLabel
                      value={"0"}
                      control={<Radio />}
                      label={`${t("NotHave")}`}
                    />
                  </RadioGroup>
                </FormControl>
              )}
            />
          </StyledWrapInput>
          <StyledWrapInput>
            <StyledContentLabel color={watch("isHavingCompensateHoliday") === "1" ? "text.third" : "#AFB1B1"} gutterBottom>
              {t("NameOfHolidayCompensate")}
            </StyledContentLabel>
            <Controller 
              control={control}
              name="compensateName"
              render={({ field }) => {
                return (
                  <TextFieldTheme
                    {...field}
                    variant="outlined"
                    multiline
                    disabled={(watch("isHavingCompensateHoliday") === "1" ? false : true) || !checkIsChosePattern()}
                    error={errors && errors.compensateName ? true : false}
                    helperText={errors && errors.compensateName && errors.compensateName.message || ""}
                  />
                );
              }}
            />
          </StyledWrapInput>
          <StyledWrapInput>
            <StyledContentLabel color={watch("isHavingCompensateHoliday") === "1" ? "text.third" : "#AFB1B1"} gutterBottom>
              {t("CompensateHolidayNameEnglish")}
            </StyledContentLabel>
            <Controller 
              control={control}
              name="compensateName_EN"
              render={({ field }) => (
                <TextFieldTheme
                  {...field}
                  variant="outlined"
                  multiline
                  disabled={(watch("isHavingCompensateHoliday") === "1" ? false : true) || !checkIsChosePattern()}
                />
              )}
            />
          </StyledWrapInput>
          <StyledWrapInput>
            <StyledContentLabel color={watch("isHavingCompensateHoliday") === "1" ? "text.third" : "#AFB1B1"} gutterBottom>
              {t("CompensateHolidayDate")}
            </StyledContentLabel>
            <Controller 
              control={control}
              name="compensateDate"
              render={({ field }) => (
                <DatePickerCustom
                  {...field}
                  openTo="day"
                  inputFormat={`DD MMM ${i18n.language === "th" ? "BBBB" : "YYYY"}`}
                  inputProps={{
                    placeholder: i18n.language === "th" ? "วว ดดด ปปปป" : "DD MMM YYYY",
                    readOnly: true
                  }}
                  disableMaskedInput
                  disabled={(watch("isHavingCompensateHoliday") === "1" ? false : true) || !checkIsChosePattern()}
                  error={errors && errors.compensateDate ? true : false}
                  helperText={errors && errors.compensateDate && errors.compensateDate.message || ""}
                />
              )}
            />
          </StyledWrapInput>
          <StyledFooter>
            <ButtonBlue variant="text" className="cancel" onClick={handleClose}>
              {t("Cancel")}
            </ButtonBlue>
            <ButtonBlue variant="contained" type="submit" autoFocus disabled={!checkIsChosePattern()}>
              {t("Save")}
            </ButtonBlue>
          </StyledFooter>
        </form>
      </StyledRoot>
    </DrawerCustom>
  );
};

export default EditHoliday;
